import React, { createContext, useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import {
   collection,
   query,
   where,
   orderBy,
   onSnapshot,
   doc,
   setDoc,
   updateDoc,
   deleteDoc,
   addDoc,
   getDoc,
   getDocs,
   writeBatch,
   serverTimestamp,
   limit,
   startAfter,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useUser } from './UserContext';
import {
   Notification,
   NotificationType,
   NotificationSettings,
   NotificationAction,
} from '../types/Notification';
import { sendSMSInvite, formatPhoneNumber, isValidPhoneNumber } from '../utils/smsUtils';
import webSocketService from '../services/WebSocketService';
import { getAllPendingInvitations, PendingInvitation } from '../services/pendingInvitationsService';
// Add MUI imports for custom notification component
import { Snackbar, Alert, Typography, useTheme, Slide } from '@mui/material';
import {
   Chat as ChatIcon,
   Group as GroupIcon,
   Person as PersonIcon,
   Info as InfoIcon,
   Flight as TripIcon,
   Event as EventIcon,
   CheckCircle as AcceptIcon,
} from '@mui/icons-material';

// Custom Notification Component
const CustomNotification = ({
   notification,
   onClose,
}: {
   notification: any;
   onClose: () => void;
}) => {
   const theme = useTheme();

   const getNotificationIcon = (type: string, emoji?: string) => {
      switch (type) {
         case 'message':
            return <ChatIcon sx={{ color: 'primary.main' }} />;
         case 'reaction':
            return (
               <Typography sx={{ fontSize: '1.5rem', minWidth: '24px', minHeight: '24px' }}>
                  {emoji || '👍'}
               </Typography>
            );
         case 'group':
            return <GroupIcon sx={{ color: 'secondary.main' }} />;
         case 'user-joined':
            return <PersonIcon sx={{ color: 'success.main' }} />;
         case 'trip-invitation':
            return <TripIcon sx={{ color: 'info.main' }} />;
         case 'event-invitation':
            return <EventIcon sx={{ color: 'warning.main' }} />;
         case 'trip-accepted':
         case 'event-accepted':
            return <AcceptIcon sx={{ color: 'success.main' }} />;
         default:
            return <InfoIcon sx={{ color: 'info.main' }} />;
      }
   };

   const getNotificationColor = (type: string) => {
      switch (type) {
         case 'message':
            return theme.palette.primary.main;
         case 'reaction':
            return theme.palette.warning.main;
         case 'group':
            return theme.palette.secondary.main;
         case 'user-joined':
            return theme.palette.success.main;
         case 'trip-invitation':
         case 'event-invitation':
            return theme.palette.info.main;
         case 'trip-accepted':
         case 'event-accepted':
            return theme.palette.success.main;
         default:
            return theme.palette.info.main;
      }
   };

   return (
      <Snackbar
         open={true}
         autoHideDuration={6000}
         onClose={onClose}
         anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
         TransitionComponent={Slide}
      >
         <Alert
            onClose={onClose}
            severity='info'
            sx={{
               minWidth: '350px',
               backgroundColor: 'background.paper',
               color: 'text.primary',
               border: `2px solid ${getNotificationColor(notification.type)}`,
               boxShadow: theme.shadows[8],
               animation: 'pulse 0.5s ease-in-out',
               '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                  '100%': { transform: 'scale(1)' },
               },
            }}
            icon={getNotificationIcon(notification.type, notification.emoji)}
         >
            <Typography variant='subtitle2' fontWeight='bold'>
               {notification.title}
            </Typography>
            <Typography variant='body2'>{notification.message}</Typography>
            {notification.details && (
               <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 1, display: 'block' }}
               >
                  {notification.details}
               </Typography>
            )}
         </Alert>
      </Snackbar>
   );
};

interface NotificationContextType {
   notifications: Notification[];
   unreadCount: number;
   totalCount: number;
   isLoading: boolean;
   error: string | null;
   // Pagination state
   hasMore: boolean;
   isLoadingMore: boolean;
   markAsRead: (notificationId: string) => Promise<void>;
   markAllAsRead: () => Promise<void>;
   deleteNotification: (notificationId: string) => Promise<void>;
   clearAllNotifications: () => Promise<void>;
   loadMoreNotifications: () => Promise<void>;
   refreshNotifications: () => Promise<void>;
   // Custom notification methods
   showCustomNotification: (
      type: string,
      title: string,
      message: string,
      emoji?: string,
      details?: string
   ) => void;
   dismissNotification: () => void;
   currentNotification: any;
   // Pending invitations count
   pendingInvitationsCount: number;
}

// Helper function to convert PendingInvitation to Notification
const convertPendingInvitationToNotification = (invitation: PendingInvitation, userId: string): Notification => {
   let type: NotificationType;
   let sourceType: 'trip' | 'event' | 'album' | 'network';
   let sourceId: string;
   let title: string;
   let message: string;
   let invitationData: any = {};

   switch (invitation.type) {
      case 'trip':
         type = 'TRIP_INVITATION';
         sourceType = 'trip';
         sourceId = invitation.tripId || '';
         title = 'Trip Invitation';
         message = `${invitation.inviterName} invited you to join "${invitation.title}"`;
         invitationData = {
            invitationId: invitation.id,
            invitationType: 'trip',
            tripId: invitation.tripId,
         };
         break;
      case 'event':
         type = 'EVENT_INVITATION';
         sourceType = 'event';
         sourceId = invitation.eventId || '';
         title = 'Event Invitation';
         message = `${invitation.inviterName} invited you to join "${invitation.title}"`;
         invitationData = {
            invitationId: invitation.id,
            invitationType: 'event',
            eventId: invitation.eventId,
         };
         break;
      case 'album':
         type = 'ALBUM_INVITATION';
         sourceType = 'album';
         sourceId = invitation.albumId || '';
         title = 'Album Invitation';
         message = `${invitation.inviterName} invited you to join "${invitation.title}"`;
         invitationData = {
            invitationId: invitation.id,
            invitationType: 'album',
            albumId: invitation.albumId,
         };
         break;
      case 'network':
         type = 'NETWORK_INVITATION';
         sourceType = 'network';
         sourceId = invitation.networkRequestId || '';
         title = 'Connection Request';
         message = `${invitation.inviterName} wants to connect with you`;
         invitationData = {
            invitationId: invitation.id,
            invitationType: 'network',
            networkRequestId: invitation.networkRequestId,
         };
         break;
   }

   return {
      id: `pending_${invitation.id}`,
      userId,
      type,
      title,
      message,
      createdAt: invitation.createdAt,
      isRead: false,
      sourceId,
      sourceType,
      sourceName: invitation.title,
      sourceImage: invitation.inviterPhotoURL,
      senderInfo: {
         id: invitation.inviterEmail || '',
         name: invitation.inviterName,
         photoURL: invitation.inviterPhotoURL,
      },
      data: {
         customMessage: invitation.customMessage,
         eventDate: invitation.eventDate,
         tripStartDate: invitation.tripStartDate,
         tripEndDate: invitation.tripEndDate,
         relationshipType: invitation.relationshipType,
         role: invitation.role,
      },
      invitationData,
      actions: [
         { type: 'ACCEPT', label: 'Accept' },
         { type: 'DECLINE', label: 'Decline' },
      ],
   };
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
   const context = useContext(NotificationContext);
   if (!context) {
      throw new Error('useNotifications must be used within a NotificationProvider');
   }
   return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
   const [notifications, setNotifications] = useState<Notification[]>([]);
   const [unreadCount, setUnreadCount] = useState(0);
   const [totalCount, setTotalCount] = useState(0);
   const [isLoading, setIsLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   // Pagination state
   const [hasMore, setHasMore] = useState(true);
   const [isLoadingMore, setIsLoadingMore] = useState(false);
   const [lastDoc, setLastDoc] = useState<any>(null);
   const { currentUser, isAuthenticated } = useUser();

   // Custom notification state
   const [currentNotification, setCurrentNotification] = useState<any>(null);
   const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
   
   // Pending invitations state
   const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
   const [pendingInvitationsCount, setPendingInvitationsCount] = useState(0);

   // Function to show custom notification with sound
   const showCustomNotification = useCallback(
      (type: string, title: string, message: string, emoji?: string, details?: string) => {
         try {
            // Clear existing notification timeout
            if (notificationTimeoutRef.current) {
               clearTimeout(notificationTimeoutRef.current);
            }

            // Play notification sound
            const playNotificationSound = () => {
               try {
                  const audio = new Audio('/audio/notification-sound.mp3'); // Correct path to audio folder
                  audio.volume = 0.3; // Set volume to 30% to avoid being too loud
                  audio.play().catch((error) => {
                     // Fallback: try a different sound from the audio folder
                     const fallbackAudio = new Audio('/audio/cheerful-memories.mp3');
                     fallbackAudio.volume = 0.3;
                     fallbackAudio.play().catch((fallbackError) => {});
                  });
               } catch (error) {
                  console.log('Error creating audio:', error);
               }
            };

            // Play sound
            playNotificationSound();

            // Show notification
            const notificationData = {
               type,
               title,
               message,
               emoji,
               details,
               timestamp: new Date(),
            };

            setCurrentNotification(notificationData);

            // Auto-dismiss after 6 seconds
            notificationTimeoutRef.current = setTimeout(() => {
               setCurrentNotification(null);
            }, 6000);
         } catch (error) {
            console.error('❌ Error details:', (error as Error)?.message);
         }
      },
      []
   );

   // Function to dismiss notification
   const dismissNotification = () => {
      setCurrentNotification(null);
      if (notificationTimeoutRef.current) {
         clearTimeout(notificationTimeoutRef.current);
      }
   };

   // Fetch notifications when user changes
   useEffect(() => {
      if (!currentUser) {
         setNotifications([]);
         setUnreadCount(0);
         setTotalCount(0);
         setIsLoading(false);
         setHasMore(false);
         setLastDoc(null);
         return;
      }

      setIsLoading(true);
      setError(null);

      try {
         // Query notifications for the current user with pagination
         const notificationsRef = collection(db, 'notifications');
         const q = query(
            notificationsRef,
            where('userId', '==', currentUser.uid),
            orderBy('createdAt', 'desc'),
            limit(20) // Initial load: 20 notifications
         );

         // Set up real-time listener
         const unsubscribe = onSnapshot(
            q,
            (snapshot: any) => {
               try {
                  const notificationsData: Notification[] = [];
                  let lastDocument = null;

                  snapshot.forEach((doc: any) => {
                     notificationsData.push({ id: doc.id, ...doc.data() } as Notification);
                     lastDocument = doc;
                  });

                  // Check for new notifications and trigger custom notifications
                  if (notifications.length > 0) {
                     const previousNotificationIds = new Set(notifications.map((n) => n.id));
                     const newNotifications = notificationsData.filter(
                        (n) => !previousNotificationIds.has(n.id) && !n.isRead
                     );

                     // Trigger custom notifications for new trip and event invitations
                     newNotifications.forEach((notification) => {
                        if (notification.type === 'TRIP_INVITATION') {
                           const tripData = notification.data || {};
                           showCustomNotification(
                              'trip-invitation',
                              'Trip Invitation',
                              `${notification.senderInfo?.name || 'Someone'} invited you to join "${tripData.tripTitle || 'a trip'}"`,
                              undefined,
                              `Date: ${tripData.tripDate || 'TBD'} | Destination: ${tripData.tripDestination || 'Unknown'}`
                           );
                        } else if (notification.type === 'EVENT_INVITATION') {
                           const eventData = notification.data || {};
                           showCustomNotification(
                              'event-invitation',
                              'Event Invitation',
                              `${notification.senderInfo?.name || 'Someone'} invited you to join "${eventData.eventTitle || 'an event'}"`,
                              undefined,
                              `Date: ${eventData.eventDate || 'TBD'} | Location: ${eventData.eventLocation || 'Unknown'}`
                           );
                        } else if (notification.type === 'TRIP_INVITATION_ACCEPTED') {
                           const tripData = notification.data || {};
                           showCustomNotification(
                              'trip-accepted',
                              'Trip Invitation Accepted',
                              `${tripData.acceptedBy || 'Someone'} accepted your invitation to "${tripData.tripTitle || 'your trip'}"`,
                              undefined,
                              `Date: ${tripData.tripDate || 'TBD'} | Destination: ${tripData.tripDestination || 'Unknown'}`
                           );
                        } else if (notification.type === 'EVENT_INVITATION_ACCEPTED') {
                           const eventData = notification.data || {};
                           showCustomNotification(
                              'event-accepted',
                              'Event Invitation Accepted',
                              `${eventData.acceptedBy || 'Someone'} accepted your invitation to "${eventData.eventTitle || 'your event'}"`,
                              undefined,
                              `Date: ${eventData.eventDate || 'TBD'} | Location: ${eventData.eventLocation || 'Unknown'}`
                           );
                        }
                     });
                  }

                  setNotifications(notificationsData);
                  setUnreadCount(notificationsData.filter((notif) => !notif.isRead).length);
                  setTotalCount(notificationsData.length);
                  setHasMore(snapshot.docs.length === 20); // Has more if we got full page
                  setLastDoc(lastDocument);
                  setIsLoading(false);
               } catch (err) {
                  console.error('Error processing notifications:', err);
                  setError('Failed to process notifications');
                  setIsLoading(false);
               }
            },
            (err: any) => {
               console.error('Error fetching notifications:', err);
               // If there's a permissions issue or the collection doesn't exist yet, handle gracefully
               if (err.code === 'permission-denied' || err.code === 'resource-exhausted') {
                  setNotifications([]);
                  setUnreadCount(0);
                  setTotalCount(0);
                  setError(null);
                  setHasMore(false);
               } else {
                  setError('Failed to load notifications');
               }
               setIsLoading(false);
            }
         );

         // Clean up listener on unmount
         return () => {
            try {
               unsubscribe();
            } catch (err) {
               console.error('Error unsubscribing from notifications:', err);
            }
         };
      } catch (err) {
         setNotifications([]);
         setUnreadCount(0);
         setTotalCount(0);
         setError(null);
         setHasMore(false);
         setIsLoading(false);
         return () => {}; // Return empty function for cleanup
      }
   }, [currentUser, showCustomNotification]);

   // WebSocket initialization and listener for real-time notifications
   useEffect(() => {
      if (!currentUser || !webSocketService.isConnected) return;

      // Register notification handler with WebSocketService
      const handleNotification = (notification: any) => {
         // Handle different notification types
         switch (notification.type) {
            case 'message':
               showCustomNotification(notification.type, notification.title, notification.message);
               break;

            case 'reaction':
               showCustomNotification(
                  notification.type,
                  notification.title,
                  notification.message,
                  notification.emoji
               );
               break;

            case 'group':
               showCustomNotification(notification.type, notification.title, notification.message);
               break;

            case 'trip_invitation':
               showCustomNotification(
                  notification.type,
                  notification.title,
                  notification.message,
                  notification.emoji,
                  notification.subtitle
               );
               break;

            case 'event_invitation':
               showCustomNotification(
                  notification.type,
                  notification.title,
                  notification.message,
                  notification.emoji,
                  notification.subtitle
               );
               break;

            case 'trip_accepted':
               showCustomNotification(
                  notification.type,
                  notification.title,
                  notification.message,
                  notification.emoji,
                  notification.subtitle
               );
               break;

            case 'event_accepted':
               showCustomNotification(
                  notification.type,
                  notification.title,
                  notification.message,
                  notification.emoji,
                  notification.subtitle
               );
               break;

            default:
               showCustomNotification(
                  notification.type || 'general',
                  notification.title || 'Notification',
                  notification.message || 'You have a new notification',
                  notification.emoji || '🔔'
               );
               break;
         }
      };

      // Register the handler
      webSocketService.setNotificationHandler(handleNotification);

      // Cleanup on unmount
      return () => {
         webSocketService.removeNotificationHandler();
      };
   }, [currentUser, showCustomNotification, webSocketService.isConnected]);

   // Fetch pending invitations and merge with notifications
   useEffect(() => {
      if (!currentUser?.email || !currentUser?.uid || !isAuthenticated) {
         setPendingInvitations([]);
         setPendingInvitationsCount(0);
         return;
      }

      const fetchPendingInvitations = async () => {
         try {
            if (!currentUser.email || !currentUser.uid || !isAuthenticated) {
               console.log('🔔 Skipping pending invitations fetch - user not authenticated');
               return;
            }
            
            console.log('🔔 Fetching pending invitations for notifications (one-time fetch)...');
            const invitations = await getAllPendingInvitations(currentUser.email, currentUser.uid);
            setPendingInvitations(invitations);
            setPendingInvitationsCount(invitations.length);
            console.log('🔔 Found', invitations.length, 'pending invitations');
            
            // Show custom notification for new invitations only when user is authenticated
            if (invitations.length > 0 && pendingInvitations.length === 0 && isAuthenticated) {
               showCustomNotification(
                  'pending-invitations',
                  'Pending Invitations',
                  `You have ${invitations.length} pending invitation${invitations.length !== 1 ? 's' : ''}`,
                  '📬'
               );
            }
         } catch (err) {
            console.error('Error fetching pending invitations:', err);
         }
      };

      // Fetch only once when user logs in
      fetchPendingInvitations();
   }, [currentUser?.email, currentUser?.uid, isAuthenticated, showCustomNotification]);

   // Merge pending invitations with regular notifications
   const mergedNotifications = useMemo(() => {
      if (!currentUser) return [];
      
      // Convert pending invitations to notifications
      const invitationNotifications = pendingInvitations.map(inv => 
         convertPendingInvitationToNotification(inv, currentUser.uid)
      );
      
      // Merge and sort by creation date (newest first)
      const merged = [...invitationNotifications, ...notifications];
      return merged.sort((a, b) => {
         const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
         const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
         return dateB.getTime() - dateA.getTime();
      });
   }, [pendingInvitations, notifications, currentUser]);

   // Update unread count to include pending invitations
   const totalUnreadCount = useMemo(() => {
      return unreadCount + pendingInvitationsCount;
   }, [unreadCount, pendingInvitationsCount]);

   // Mark a single notification as read
   const markAsRead = async (notificationId: string) => {
      if (!currentUser) return;

      try {
         const notificationRef = doc(db, 'notifications', notificationId);
         await updateDoc(notificationRef, { isRead: true });
      } catch (err) {
         console.error('Error marking notification as read:', err);
         setError('Failed to update notification');
      }
   };

   // Mark all notifications as read
   const markAllAsRead = async () => {
      if (!currentUser) return;

      try {
         const batch = writeBatch(db);

         notifications.forEach((notification) => {
            if (!notification.isRead) {
               const notificationRef = doc(db, 'notifications', notification.id);
               batch.update(notificationRef, { isRead: true });
            }
         });

         await batch.commit();
      } catch (err) {
         console.error('Error marking all notifications as read:', err);
         setError('Failed to update notifications');
      }
   };

   // Delete a notification
   const deleteNotification = async (notificationId: string) => {
      if (!currentUser) return;

      try {
         const notificationRef = doc(db, 'notifications', notificationId);
         await deleteDoc(notificationRef);
      } catch (err) {
         console.error('Error deleting notification:', err);
         setError('Failed to delete notification');
      }
   };

   // Clear all notifications
   const clearAllNotifications = async () => {
      if (!currentUser) return;

      try {
         const batch = writeBatch(db);

         notifications.forEach((notification) => {
            const notificationRef = doc(db, 'notifications', notification.id);
            batch.delete(notificationRef);
         });

         await batch.commit();
      } catch (err) {
         console.error('Error clearing notifications:', err);
         setError('Failed to clear notifications');
      }
   };

   // Load more notifications for pagination
   const loadMoreNotifications = async () => {
      if (!currentUser || !hasMore || isLoadingMore || !lastDoc) {
         return;
      }

      setIsLoadingMore(true);

      try {
         const notificationsRef = collection(db, 'notifications');
         const q = query(
            notificationsRef,
            where('userId', '==', currentUser.uid),
            orderBy('createdAt', 'desc'),
            startAfter(lastDoc),
            limit(20)
         );

         const snapshot = await getDocs(q);
         const newNotifications: Notification[] = [];
         let newLastDoc = null;

         snapshot.forEach((doc) => {
            newNotifications.push({ id: doc.id, ...doc.data() } as Notification);
            newLastDoc = doc;
         });

         if (newNotifications.length > 0) {
            setNotifications((prev) => [...prev, ...newNotifications]);
            setTotalCount((prev) => prev + newNotifications.length);
            setLastDoc(newLastDoc);
            setHasMore(snapshot.docs.length === 20);
         } else {
            setHasMore(false);
         }
      } catch (err) {
         console.error('Error loading more notifications:', err);
         setError('Failed to load more notifications');
      } finally {
         setIsLoadingMore(false);
      }
   };

   // Refresh notifications (reload from beginning)
   const refreshNotifications = async () => {
      if (!currentUser) return;

      setIsLoading(true);
      setError(null);

      try {
         const notificationsRef = collection(db, 'notifications');
         const q = query(
            notificationsRef,
            where('userId', '==', currentUser.uid),
            orderBy('createdAt', 'desc'),
            limit(20)
         );

         const snapshot = await getDocs(q);
         const notificationsData: Notification[] = [];
         let lastDocument = null;

         snapshot.forEach((doc) => {
            notificationsData.push({ id: doc.id, ...doc.data() } as Notification);
            lastDocument = doc;
         });

         setNotifications(notificationsData);
         setUnreadCount(notificationsData.filter((notif) => !notif.isRead).length);
         setTotalCount(notificationsData.length);
         setHasMore(snapshot.docs.length === 20);
         setLastDoc(lastDocument);
      } catch (err) {
         console.error('Error refreshing notifications:', err);
         setError('Failed to refresh notifications');
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <NotificationContext.Provider
         value={{
            notifications: mergedNotifications,
            unreadCount: totalUnreadCount,
            totalCount: totalCount + pendingInvitationsCount,
            isLoading,
            error,
            hasMore,
            isLoadingMore,
            markAsRead,
            markAllAsRead,
            deleteNotification,
            clearAllNotifications,
            loadMoreNotifications,
            refreshNotifications,
            showCustomNotification,
            dismissNotification,
            currentNotification,
            pendingInvitationsCount,
         }}
      >
         {children}
         {/* Custom Notification Display */}
         {currentNotification && (
            <CustomNotification notification={currentNotification} onClose={dismissNotification} />
         )}
      </NotificationContext.Provider>
   );
};
